import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/useTranslations";
import { useTransition, type FormEvent } from "react";

import { Button } from "@/components/ui/Button";
import { buildEventLibraryHref } from "@/core/services/types/event.types";
import type {
  EventLibraryCategoryFilter,
  EventLibraryDateFilters,
  EventLibraryTab,
  EventLibrarySortState,
} from "@/core/services/types/event.types";
import type { LibraryPageSize } from "@/core/services/types/library-pagination.types";

const searchInputClassName =
  "w-full rounded-xl bg-zinc-50 px-3 py-2 text-sm text-whart-text outline-none placeholder:text-neutral-400 focus:bg-zinc-100 disabled:opacity-50";

function SearchSpinner() {
  return (
    <span
      aria-hidden
      className="inline-block size-3.5 animate-spin rounded-full border-2 border-zinc-900 border-r-transparent"
    />
  );
}

interface EventsLibrarySearchFormProps {
  tab: EventLibraryTab;
  category: EventLibraryCategoryFilter;
  dateFilters: EventLibraryDateFilters;
  searchQuery: string;
  sort: EventLibrarySortState;
  pageSize: LibraryPageSize;
}

function buildLibraryHref(
  tab: EventLibraryTab,
  category: EventLibraryCategoryFilter,
  dateFilters: EventLibraryDateFilters,
  sort: EventLibrarySortState,
  pageSize: LibraryPageSize,
): string {
  return buildEventLibraryHref({ tab, category, dateFilters, page: 1, sort, pageSize });
}

export function EventsLibrarySearchForm({
  tab,
  category,
  dateFilters,
  searchQuery,
  sort,
  pageSize,
}: EventsLibrarySearchFormProps) {
  const t = useTranslations("events.library");
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition();

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const query = String(formData.get("q") ?? "").trim();

    startTransition(() => {
      navigate(
        buildEventLibraryHref({
          tab,
          category,
          dateFilters,
          page: 1,
          searchQuery: query,
          sort,
          pageSize,
        }),
      );
    });
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-3 py-1 sm:flex-row sm:items-center"
    >
      <label className="sr-only" htmlFor="events-library-search">
        {t("searchLabel")}
      </label>
      <input
        id="events-library-search"
        type="search"
        name="q"
        defaultValue={searchQuery}
        placeholder={t("searchPlaceholder")}
        disabled={isPending}
        className={searchInputClassName}
      />
      <div className="flex shrink-0 items-center gap-2">
        <Button
          type="submit"
          disabled={isPending}
          aria-busy={isPending}
        >
          {isPending ? <SearchSpinner /> : null}
          {t("searchSubmit")}
        </Button>
        {searchQuery ? (
          <Link
            to={buildLibraryHref(tab, category, dateFilters, sort, pageSize)}
            className="inline-flex items-center rounded-xl px-4 py-2 text-sm font-medium text-neutral-600 transition-colors hover:bg-zinc-50 hover:text-zinc-900"
          >
            {t("clearSearch")}
          </Link>
        ) : null}
      </div>
    </form>
  );
}
